/**
 * CustomerLoginPage
 *
 * Clean, minimal customer-facing login page.
 * Loads branding (logo, primaryColor, name) from the platform config
 * via PortalContext. Falls back to default NodeGeeks branding when
 * the platform has no branding configured.
 *
 * Does NOT use Cloudscape components — uses custom CSS modules instead.
 *
 * Requirements: 3.1, 3.2, 3.3, 6.2, 6.4
 */

import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { CustomerBranding, DEFAULT_BRANDING } from '../components/customer';
import { CustomerLoginForm } from '../components/customer';
import { useAuth } from '../contexts/AuthContext';
import { usePortal } from '../contexts/PortalContext';
import styles from '../components/customer/CustomerLogin.module.css';

export const CustomerLoginPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, login, isLoading: authLoading, requiresProfileSelection } = useAuth();
  const { branding } = usePortal();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Success message from registration redirect
  const successMessage = (location.state as { message?: string } | null)?.message || null;
  const [showSuccess, setShowSuccess] = useState<string | null>(successMessage);

  // OAuth parameters
  const clientId = searchParams.get('client_id');
  const isOAuthFlow = Boolean(clientId);

  // Resolve brand color for CSS custom property
  const primaryColor = branding?.primaryColor || DEFAULT_BRANDING.primaryColor;

  // ── Post-auth redirects (mirrors AgentLoginPage logic) ──────────

  useEffect(() => {
    if (isAuthenticated && isOAuthFlow && !requiresProfileSelection) {
      navigate(`/oauth/authorize?${new URLSearchParams(searchParams).toString()}`);
    }
  }, [isAuthenticated, isOAuthFlow, requiresProfileSelection, navigate, searchParams]);

  useEffect(() => {
    if (isAuthenticated && requiresProfileSelection) {
      const params = isOAuthFlow ? `?${new URLSearchParams(searchParams).toString()}` : '';
      navigate(`/select-profile${params}`);
    }
  }, [isAuthenticated, requiresProfileSelection, isOAuthFlow, navigate, searchParams]);

  useEffect(() => {
    if (isAuthenticated && !isOAuthFlow && !requiresProfileSelection) {
      navigate('/account');
    }
  }, [isAuthenticated, isOAuthFlow, requiresProfileSelection, navigate]);

  // ── Handlers ────────────────────────────────────────────────────

  const handleLogin = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = () => navigate('/forgot-password');
  const handleCreateAccount = () => navigate('/register');

  // ── Loading state ───────────────────────────────────────────────

  if (authLoading) {
    return (
      <div className={styles.loadingPage} role="status" aria-label="Loading">
        Loading…
      </div>
    );
  }

  // ── Render ──────────────────────────────────────────────────────

  return (
    <div
      className={styles.page}
      style={{ '--brand-primary': primaryColor, '--brand-focus': primaryColor } as React.CSSProperties}
    >
      <main className={styles.card} id="main-content">
        <CustomerBranding branding={branding} />

        {showSuccess && (
          <div className={`${styles.alert} ${styles.alertSuccess}`} role="status" style={{ marginBottom: '1rem' }}>
            {showSuccess}
          </div>
        )}

        <CustomerLoginForm
          onSubmit={handleLogin}
          isLoading={isLoading}
          error={error}
          onClearError={() => { setError(null); setShowSuccess(null); }}
          onForgotPassword={handleForgotPassword}
          onCreateAccount={handleCreateAccount}
        />
      </main>
    </div>
  );
};

export default CustomerLoginPage;
